/** Shared authentication context so all components see the same user state. */

import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useAuth } from "./useAuth";
import { setOnUnauthorized } from "../services/api";

type AuthContextValue = ReturnType<typeof useAuth>;

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const auth = useAuth();
  // Wire the API client's 401 handler so a stale/expired session forces a
  // logout and returns the user to the login page instead of silently failing.
  useEffect(() => {
    setOnUnauthorized(() => {
      auth.logout().catch(() => {
        /* logout best-effort; cookie may already be gone */
      });
    });
    return () => setOnUnauthorized(null);
  }, [auth.logout]);
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return ctx;
}
